/**
 * <site-drawer> — the shared slide-in panel used by the bag, search and account drawers.
 *
 * Markup contract:
 *   <site-drawer name="cart" hidden>
 *     <div class="drawer__backdrop" data-drawer-close></div>
 *     <aside class="drawer__panel" role="dialog" aria-modal="true" aria-label="…"> … </aside>
 *   </site-drawer>
 *
 * Anything in the page that carries data-drawer-open="<name>" opens the matching drawer, so the
 * header's Search and Bag links keep working as plain links when scripting is unavailable.
 * Escape, the backdrop and [data-drawer-close] close it. Focus moves into the panel on open,
 * is kept inside it while open, and returns to the element that opened it on close.
 */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

class SiteDrawer extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('.drawer__panel');
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    SiteDrawer.registry.set(this.getAttribute('name'), this);
  }

  disconnectedCallback() {
    SiteDrawer.registry.delete(this.getAttribute('name'));
    if (this.open) this.unlockScroll();
  }

  static registry = new Map();

  static get(name) {
    return SiteDrawer.registry.get(name);
  }

  get open() {
    return !this.hidden;
  }

  onClick = (event) => {
    if (event.target.closest('[data-drawer-close]')) {
      event.preventDefault();
      this.close();
    }
  };

  onKeydown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== 'Tab' || !this.panel) return;
    const items = [...this.panel.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  show(opener) {
    if (this.open) return;
    this.opener = opener ?? document.activeElement;
    this.hidden = false;
    this.lockScroll();
    requestAnimationFrame(() => {
      this.classList.add('drawer--open');
      const target = this.panel?.querySelector('[data-drawer-focus]') ?? this.panel?.querySelector(FOCUSABLE);
      target?.focus();
    });
    this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
  }

  close() {
    if (!this.open) return;
    this.classList.remove('drawer--open');
    this.hidden = true;
    this.unlockScroll();
    this.opener?.focus?.();
    this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
  }

  lockScroll() {
    document.documentElement.style.overflow = 'hidden';
  }

  unlockScroll() {
    document.documentElement.style.overflow = '';
  }
}

if (!customElements.get('site-drawer')) customElements.define('site-drawer', SiteDrawer);

document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-drawer-open]');
  if (!opener) return;
  const drawer = SiteDrawer.get(opener.dataset.drawerOpen);
  if (!drawer) return; // no drawer on the page: let the link navigate
  event.preventDefault();
  if (drawer.open) drawer.close();
  else drawer.show(opener);
});

export { SiteDrawer };
